import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, X } from 'lucide-react';

/**
 * GhostBanner — compact floating pill (iter128.3) shown in the top-right
 * of the viewport whenever an admin is impersonating another user.
 *
 * Reads ``sessionStorage.harmonix_ghost_meta`` which is set by
 * ``handleGhostLogin`` (AdminPage) or ``ghostLoginAsOwner``
 * (AdminAgentSearch). Clicking the pill restores the admin session
 * stashed in ``sessionStorage.harmonix_ghost_return``.
 *
 * Previously a full-width sticky top bar; the pill is now anchored via
 * ``fixed`` positioning so it's always in reach without adding vertical
 * chrome to every page.
 */
export const GhostBanner = () => {
  const [meta, setMeta] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const read = () => {
      try {
        const raw = sessionStorage.getItem('harmonix_ghost_meta');
        setMeta(raw ? JSON.parse(raw) : null);
      } catch { setMeta(null); }
    };
    read();
    // Re-check when the page regains focus (e.g., after switching tabs
    // where the admin ghost-logged in).
    const onFocus = () => read();
    window.addEventListener('focus', onFocus);
    // Live-refresh the "expires in Xm" every 30s.
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(tick);
    };
  }, []);

  if (!meta) return null;

  const exit = () => {
    const adminTok = sessionStorage.getItem('harmonix_ghost_return');
    if (adminTok) {
      localStorage.setItem('harmonix_st', adminTok);
    } else {
      localStorage.removeItem('harmonix_st');
    }
    sessionStorage.removeItem('harmonix_ghost_return');
    sessionStorage.removeItem('harmonix_ghost_meta');
    toast.success('Exited ghost mode');
    window.location.href = '/dashboard/admin?tab=users';
  };

  const expiresAt = meta.expires_at ? new Date(meta.expires_at).getTime() : null;
  const expiresIn = expiresAt ? Math.max(0, Math.round((expiresAt - now) / 60000)) : null;

  return (
    <button
      type="button"
      onClick={exit}
      data-testid="ghost-mode-exit-pill"
      className="fixed top-3 right-3 z-[100] flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 shadow-lg border border-blue-500 text-xs sm:text-sm transition-colors cursor-pointer"
      title={`Ghost mode — viewing as ${meta.target_email}. Click to exit.`}
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-300 opacity-70"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
      </span>
      <Eye className="w-3.5 h-3.5" />
      <span className="hidden sm:inline font-medium">Ghost:</span>
      <span className="truncate max-w-[10rem] sm:max-w-[16rem]">{meta.target_email}</span>
      {expiresIn !== null && (
        <span className="hidden sm:inline opacity-80 text-[10px]">· {expiresIn}m</span>
      )}
      <span className="mx-0.5 opacity-40">|</span>
      <span className="flex items-center gap-1 font-medium">
        <X className="w-3.5 h-3.5" /> Exit
      </span>
    </button>
  );
};

export default GhostBanner;
